/**
 * Contenu du fichier armaor.json
 */
export interface LocalInitFile {
  ARMASERV_IP: string;
  ARMASERV_PORT: string;
  HTTPS: boolean;
}

/**
 * Pour la gestion du décorticage en ligne.
 * Contiens les infos nessecaire pour gerer la forme 99, Utiliser dans JSONLineDec
 * Seulement angle d'utiliser si on a l'angle de départ
 */
export interface Shape99InfoLine {
  Radius: string; //
  Length: string; //
  Angle: string;  //
}

export interface Chantier {
  CODECHANTIER: string;
  NOM: string;
  /**
   * Correspond au code usine.
   * - 0 quand le chantier n'a pas d'usine de définit
   */
  DEFAULT_FACTORY: number;
}

/**
 * Retour typique lors de l'appel d'un fonction DataSnap Rest
 * 'result[0]'      - quand la methode a fonctionne
 * 'error'          - quand la methode appeler retourne une erreur (pase dans le throw en js)
 * 'SessionExpired' - quand la session n'est plus active coté serveur
 */
export interface DataSnapResult {
  result: [any];
  error?: string;
  SessionExpired?: string;   // N'est j'amais trigger comme tel car le serveur retourne un status http 403, donc catch d'un HTTPErrorResponse
}

export interface DataSnapSessionInfo {
  dssession: string;                  //
  dssessionexpires: number;            // En miliseconde
}

export interface DataSnapResultCertificats {
  result: [Certificate[]];
}

export interface LoggedUserData {
  ID_LOGGED: any;
  LOGIN_TYPE: number;
  CLIENT_CODE: string; // Vide quand on est connecté en tant qu'utilisateur armaor
  USER_NAME: string;
  RightOverview: boolean;
  RightWebInput: boolean;
  RightWebSiteFollowup: boolean;
  RightOrderList: boolean;
  RightDeliveryList: boolean;
  RightCertif: boolean;
  RightInvoiceList: boolean;
  RightDataList: boolean;
  RightSupervisor: boolean;
  RightStock: boolean;
  IsSuperAdmin: boolean;
  Dashboard: DashBoard[];
}

export interface HomeScreenElement {
  visible: boolean;                       //
  title: string;
  imageSrc: string;
  content: string;
  onClickEventFunc: () => void;
}

export interface Certificate {
  DOC_NAME: string; // Nom du fichier
  DOC_PATH: string;  // Chemin du fichier à telecharger
}

export interface ArmaRestAnalysis {
  OrderCode: string;            // Code dec
  extOrderCode: string;         // Code dec ext BESIX/CICON
  JoiningCode: number;          // Joining code
  ClientRef: string;            //
  Drawing: string;              // GetLibDwg
  Designation: string;          // AfDesiplan
  PersonInCharge: string;
  CreationDate: string;
  UnixCreationDate: number;
  PlannedDelivDate: string;
  UnixPlannedDelivDate: number;
  Weight: number;
  Production: string;           // String du status de production
  Delivery: string;             // String du status de livraison
  DeliveryStatus: number;
  LoadingStatus: number;
  ProductionStatus: number;
  IsOrderApproved: boolean;
}

export interface ArmaRestInfoComp {
  AnalysisCode: string;
  ArrayInfoComp: {
    INFO_LIBELLE: string;
    INFO_TYPE: number;
    VALEUR: string;
  }[];
}

export interface UrlParam {
  name: string;
  value: string;
}

export interface Grade {
  Name: string;
  ShortName: string;
  Code: string;
  DiameterAvailable: number[];
  Default: number;             // 0 ou 1
}

export interface DashBoard {
  Name: string;
  NoDash: number;
  Count: number;
}

export interface ErrorLineDec {
  /**
   * Numéro de ligne sur armaor
   */
  NoLine?: number;
  /**
   * Numéro de ligne affiché sur le site web
   */
  NoLineWeb: number;
  Mess: string;
  IsBlocking: number;
}

// Utiliser pour gerer les parametre de l'utilisateur, préveu pour etre stocké dans le local storage de l'app,
// Fonction dans utilit pour recuperer/envoyer depuis le local storage
// Type number non accepté, il faut passé par un string
export interface UserParam {
  // Analysis
  /** Default: true*/         ShowMemberColumn: boolean;
  /** Default: "Classical" */ AnalysisInputType: 'Classical'|'BS 4466 / 8666';
  /** Default: false*/        InputPreviousShape: boolean;
  /** Default: false*/        InputPreviousDiamter: boolean;
  /** Default: false*/        InputPreviousGrade: boolean;
  /** Default: false*/        InputPreviousQty: boolean;
  /** Default: false*/        InputPreviousMeasure: boolean;
  /** Dedault: "none"*/       InputPreviousBM: 'none'|'number'|'letter';           // "0" = Non, "1",= Suite de chiffre (1...), "2" = Suite de lette (A...)
  /** Default: true*/         InputDistribution: boolean;
  /** Default: Full */        ImageType: 'Simple'|'Full';
  /** Default: false */       SteelStock: boolean;
  /** Default: false */       AnalysisHideComment: boolean;
}

export interface CustomModalData {
  Title: string;
  Message: string[];
  Button: {
    ButtonText: string,
    ResultValue: any      // Resultat attendu quand on va cliquer sur le dit bouton
  }[];
}

/** Gere la visibilité de tout les element de la toolbar */
export interface VisibilityElementToolbar {
  // Dans l'ordre d'apparition
  ArmaorLogo?: boolean;
  ArrayStartBtn?: boolean;
  Title?: boolean;
  SiteSelect?: boolean;
  ArrayEndBtn?: boolean;
  MenuBtn?: boolean;
}

/**
 * Contiens les informations d'une liste d'acier
 * Correspond au json que l'on recuperer depuis armaserve (doit etre identique)
 * -> ConvertToJS_SteelList
 */
export interface ArmaRestOrderList {
  Code: string;
  IDDecorticage: string;
  CodeSite: string;
  CodeClient: string;
  Designation: string;
  Designation2: string;
  NumPlan: string;
  IndicePlan: string;
  Divers: string;
  RefExterne: string;           // On stock le code contrat utilisé quandon importe la liste d'acier web
  DateCreate: string;
  UnixDateCreate: number;
  DateDelivery: number; // La date est en UNIXTimestamp
  Status: string;
  /** @see SteelListStatus */
  StatusInteger: number;
  IDCatalogue: string;
  Societe: string;
  CanEdit: number;
  CustomerRef: string;
  OrderJoiningCode: number;
  IdContact?: number;
  Note?: string;
}

export interface Formes {
  CleForme: number;       // Correspond à elem dans armaor CSHAPE ?
  CodeForme: string;      // String
  Cote: CoteForme[];      // Array
  Trace: TraceForme[];    // Array
  WebSelectable: number;  // 0 false | 1 true

  NbPliure: number;       //
  NbCote: number;         //
  NbCintrage: number;     // Nb Arc de cercle
  Is3DForme: boolean;     //
  IsRay: boolean;         // Vrai si la forme contient une cote rayon
  IsDiam: boolean;        // Vrai si la forme contient une cote diametre
  HasCoupler: boolean;    // Vrai si la forme est décrite avec une cote coupleur de début ou de fin
  NbAngle180: number;     //
  NbAngle90: number;      //
  NbAngle135: number;     //
  NbAngle45: number;      //
}

export interface TraceForme {
  ID: string; // Int
  TypeTrace: string; // ITypeCote
  Pt1_X: number; // Int
  Pt1_Y: number; // Int
  Pt2_X: number; // Int
  Pt2_Y: number; // Int
  Pt3_X: number; // Int
  Pt3_Y: number; // Int
  Comment: string;
}

/**
* Interface ou est stockées les emplacements et types de cote pour une forme
*/
export interface CoteForme {
  LFormule: string;
  LCote: string;
  /**
   * @see CALC_VISIBILITY_COTE
   */
  Calc: string;
  TypeCote: number;
  //dummy: string;
  Region_Width: string;
  Region_Height: string;
  Region_Left: number;
  Region_Top: number;
  Region_Right: number;
  Region_Bottom: number;
  Region_TopLeft_X: string;
  Region_TopLeft_Y: string;
  Region_BottomRight_X: string;
  Region_BottomRight_Y: string;
  PC3_X: number;
  PC3_Y: number;
}

/**
 * Status de forme favorite
 */
export enum FavoriteShapeStatus {
  Default = -1,   // Pas favorite
  Favorite,   // Forme Favorite
  PanelVisible // Forme visible dans le panneau de formes
}

/**
 * Structure de forme favorite utilisé dans la liste des formes favorite (Lié au client connecté)
 */
export interface FavoriteShape {
  NoForme: number;
  Status: FavoriteShapeStatus;
  PanelVisible: boolean;
}

export interface LoadedAnalysis {
  arrayLineDec: JsLineDec[];
  shapeAvailable: Formes[];
  shapeFavorite: FavoriteShape[];
  infoSteelList: ArmaRestOrderList;
}

/**
 * Correspond au json recu par datasnap lors de la recuperation d'une ligne d'un décorticage
 */
 export interface JsLineDec {
  readonly idLineDB: number;  //oLineD.Part6.IDLine // Identifiant unique de la line recup depuis le armaor, undefine si ligne crée depuis le web
  Elem: number;               // Correspond au cleforme dans la shape, permet faire le calcule des cotes donc nessecaire ici + bessoins pour conversion cm to mm

  NoLine: string;             // [int] Numéro de la ligne à afficher sur le web (sans la ligne d'elem)

  ErrorLine?: string;      // Erreur calculer depuis le serveur sur la ligne
  SmallComment?: string;   // A convertir sur delphi, n'appartiens pas spécificquement a une steelligne donc valeur stocké ici

  MemberInfo?: JsonLineMember; // Informations de l'element, ici les lignes d'elem sont fusioné aux autres lignes

  // Un seule type de ligne doit etre remplis ici, undefine pour le reste
  SteelLine?: JsLineSteel;     // Ligne d'acier (ligne strandart / ligne 99)
  ProductLine?: JsLineProduct; // Ligne de produits
  RepartLine?: JsLineRepart;   // Ligne de répartition
  WMeshLine?: JsLineWMesh;     // Ligne welded mesh
}

/**
 * Ligne d'element sur armaor
 * La ligne d'element sur le web est fusioné aux autres lignes
 */
export interface JsonLineMember {
  MemberName: string;     // String
  /**Pas de gestion du type de l'element pour le moment, toujors valeur 0 ici */
  //MemberType: string;     // Integer
  /**Pas de gestion de la quantité de l'element, toujours valeur 1 ici */
  MemberQty: string;      // Integer
}

/**
 * Une ligne d'acier ou ligne99
 */
 export interface JsLineSteel {
  Rep: string;            // String
  Qteb: number;           // Int
  Diam: number;           // String
  Grade: string;

  CodeF: string;          // String

  Cote: string[];
  DataShape99: Shape99InfoLine[]; // Contiens un tableau d'interface avec pour chaque indice radius, lenght, angle (seulement angle pour l'indice0 angle de départ)

  Mach1: number;          // Byte
  NameMach1: string;      // String
  Mach2: number;          // Byte
  NameMach2: string;      // String
  Mach3: number;          // Byte
  NameMach3: string;      // String

  PoidsCoupe: number;     // Double
  PoidsClient: number;    // Double
  Lgclient: string;       // Double
  Lgcoupe: string;        // Double
}

/**
 * Une ligne de produit
 */
 export interface JsLineProduct {
  ProductCode: string;
  ProductLabel: string;
  ProductQty: number;
  ProductU1: string;
  ProductU2: string;
  ProductUnitUsed: string;
  Rep: string;            // String
  ProductU1Weight: number;
  ProductU1ToU2: number;
}

/**
 * Une ligne de répartition
 */
 export interface JsLineRepart {
  Repartition: string;    //String contient la description de la répartition
  FilantRepart: number;   //Int Index des répartitions liées (5 = 101 = répart 1 & 3)
  RepartCrochetLevage: boolean; // boolean
}

/**
 * Une ligne de wmesh
 */
export interface JsLineWMesh {
  itemNumber: number;
  itemname: string;
  remark: string;
  quantity: number;
  barmark: string;
  /**Correspond au Y */
  vertical: WMeshAxesInformation;
  /**Correspond au X */
  horizontal: WMeshAxesInformation;
  totalWeightRounded: number;
}

/**
 * Information d'un axe sur le wmesh
 */
export interface WMeshAxesInformation {
  nbBar: number;
  step: number;
  diameter?: string;
  startValue: number;
  endValue: number;
}

export interface ArmaRestDnList {
  DOC_NO: string;     // codeBL
  DOC_DATE: string;
  UnixBLDate: number;
  DOC_LIB: string;    // Ref BL
  DOC_VALUE: string;  // Steel + product weight
  DOC_CUST_NAME: string;
  DOC_SITE_NAME: string;
  ArrayOrder: {
    extAnalysisCode: string; // Code decorticage extern (pour BESIX CICON)
    analysisCode: string;
    analysisWeight: number;
    designation: string;
    planIdPlan: string;
  }[];
}

export interface SteelListValidity {
  NoDeliveryDate?: boolean;
  NoCustomerRef?: boolean;
  NoUniqueCustomerRef?: boolean;
  customMessage?: string;
  NoContact?: boolean;
}

export interface ArmaRestResultSteelListSend extends SteelListValidity {
  IsSend: boolean;
}

export type ContactNoId = Omit<ArmaRestContact, 'Id'>;
export interface ArmaRestContact {
  Id: number;
  Name: string;
  LastName: string;
  Phone: string;
  Email: string;
}

export interface CustomTableHeader {
  Id: number;
  Name: string;
}

export interface PrintingList {
  Header: CustomTableHeader[];
  Tab: any[];
}

/**
 * Recup depuis l'objet "TInfoFormatXML" d'Armaor
 */
export interface PrintingListInfoFormatData {
  IDFormat: number;
  LibelleFormat: string;
  AvailableForWeb: boolean;
}
